import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  getInfosFromDeltafaktoren,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  getTarifbereich,
  Produktlinie,
  VermietetesModalObjekt,
  VermietetesObjektEnum,
  VermietetesObjektQuoteDB,
  VermietetesUnbebautesModalObjekt,
} from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
} from '@ps20-frontend/preis-lib';
import { Dialogschritt, EinheitenArt, TaasRequestModel, TaasRsVmData } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { RsVermieterActions } from '../rs-vermietung-actions.service';
import { IAppState } from '../appState';
import { buildTaasRSVmDataForStrecke } from './streckenTarifierungVmRS';
import { buildTaasRsDataForShop } from './shopTarifierungVmRS';

export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.RS_VERMIETUNG));
  yield takeLatest(RsVermieterActions.QUOTEDB_REQUESTED_RS_VERMIETUNG, safeSaga(quoteDbInit, App.RS_VERMIETUNG));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  let taasRequestModel: TaasRequestModel<TaasRsVmData>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;
  const spartenAngebotsId: AngebotsId = { appId: App.RS_VERMIETUNG };
  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }

  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen?.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasRsVmData> = {
    angebotsId: { appId: App.RS_VERMIETUNG },
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis:
      action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN
        ? state.gesamtbeitrag
        : state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.ausgewaehltevertragslaufzeit,
    antragsnummer: state.antragsnummer,
    vermietetesObjekt: convertVermieteteObjekteQuoteDb(state),
  };

  getInfosFromDeltafaktoren(state.taasInfo, state.vertragsoptionen, quoteDbPayload);

  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: { appId: App.RS_VERMIETUNG },
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasRsVmData> {
  const taasData: TaasRsVmData = buildTaasRSVmDataForStrecke(state);
  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen),
    taasData,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasRsVmData> {
  const rsVmData: TaasRsVmData = buildTaasRsDataForShop(state);

  const newHashedTaasRequest = createHashedTaasData(rsVmData);
  return buildTaasRequestModelForShop(
    rsVmData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function convertVermieteteObjekteQuoteDb(state: IAppState): Array<VermietetesObjektQuoteDB> {
  const vermieteteObjekte: Array<VermietetesModalObjekt> = state.vermieteteCardObjekte;
  if (!vermieteteObjekte) {
    return;
  }
  const vermieteteEinheiten: Array<VermietetesObjektQuoteDB> =
    new Array<VermietetesObjektQuoteDB>();
  vermieteteObjekte.forEach((vermietetesObjektFrontend, index) => {
    const sachortId = index + 1;

    if (vermietetesObjektFrontend.grundstueckBebaut === VermietetesObjektEnum.BEBAUT) {
      if (vermietetesObjektFrontend.wohneinheiten && vermietetesObjektFrontend.bruttojahresmieteWohneinheiten) {
        vermieteteEinheiten.push({
          id: sachortId,
          plzSachort: vermietetesObjektFrontend.plz,
          jahresBruttoMiete: vermietetesObjektFrontend.bruttojahresmieteWohneinheiten,
          einheitenArt: EinheitenArt.WOHNEINHEIT,
          anzahlVermieteteEinheiten: vermietetesObjektFrontend.wohneinheiten,
        });
      }
      if (vermietetesObjektFrontend.gewerbeeinheiten && vermietetesObjektFrontend.bruttojahresmieteGewerbeeinheiten) {
        vermieteteEinheiten.push({
          id: sachortId,
          plzSachort: vermietetesObjektFrontend.plz,
          jahresBruttoMiete: vermietetesObjektFrontend.bruttojahresmieteGewerbeeinheiten,
          einheitenArt: EinheitenArt.GEWERBEEINHEIT,
          anzahlVermieteteEinheiten: vermietetesObjektFrontend.gewerbeeinheiten,
        });
      }
      if (vermietetesObjektFrontend.stellplaetze && vermietetesObjektFrontend.bruttojahresmieteStellplaetze) {
        vermieteteEinheiten.push({
          id: sachortId,
          plzSachort: vermietetesObjektFrontend.plz,
          jahresBruttoMiete: vermietetesObjektFrontend.bruttojahresmieteStellplaetze,
          einheitenArt: EinheitenArt.GARAGE,
          anzahlVermieteteEinheiten: vermietetesObjektFrontend.stellplaetze,
        });
      }
    } else {
      const unbebautesObjekt = vermietetesObjektFrontend as VermietetesUnbebautesModalObjekt;
      vermieteteEinheiten.push({
        id: sachortId,
        plzSachort: unbebautesObjekt.plz,
        einheitenArt: EinheitenArt.UNBEBAUTES_GRUND_STUECK,
        unbebauteFlaeche: unbebautesObjekt.grundstueckFlaeche,
      });
    }
  });
  return vermieteteEinheiten;
}
